import type { FaceKey, FacePhotoSize } from '@repo/api-contracts';
import type { EditAreaDraftState } from '../model/edit-area-draft-types';

/** 그 면 마스크와 px 크기가 다른 합성 에셋 또는 색상 사진. photo 는 어느 색상의 사진인지 함께 준다. */
export type CompositeSizeMismatch =
  | {
      readonly asset: 'shading' | 'safeArea';
      readonly size: FacePhotoSize;
      readonly maskSize: FacePhotoSize;
    }
  | {
      readonly asset: 'photo';
      readonly colorOptionId: string;
      readonly size: FacePhotoSize;
      readonly maskSize: FacePhotoSize;
    };

const isSameSize = (a: FacePhotoSize, b: FacePhotoSize): boolean =>
  a.widthPx === b.widthPx && a.heightPx === b.heightPx;

/**
 * 면별 합성 크기 불일치(Q9) — 마스크와 그 면의 음영·안내선·모든 색상 사진을 비교한다. 순서는 계약 교차 검사
 * (음영 → 안내선 → 색상 순)와 같다. 합성 카드의 경고와 저장 전 안내가 이 함수를 같이 쓰고, 저장 차단은 계약
 * 검사가 한다. 마스크가 없는 면은 비교할 기준이 없어 빈 목록이다.
 */
export const compositeSizeMismatchesOf = (
  state: Pick<EditAreaDraftState, 'faces' | 'draft'>,
  face: FaceKey,
): CompositeSizeMismatch[] => {
  const composite = state.faces[face]?.composite;
  if (composite === undefined) return [];
  const { maskSize } = composite;
  const assets: CompositeSizeMismatch[] = [
    ...(composite.shadingSize === undefined || isSameSize(composite.shadingSize, maskSize)
      ? []
      : [{ asset: 'shading' as const, size: composite.shadingSize, maskSize }]),
    ...(composite.safeAreaSize === undefined || isSameSize(composite.safeAreaSize, maskSize)
      ? []
      : [{ asset: 'safeArea' as const, size: composite.safeAreaSize, maskSize }]),
  ];
  const photos = state.draft.colors.flatMap((color): CompositeSizeMismatch[] => {
    const photo = color.faces[face];
    return photo === undefined || isSameSize(photo.photoSize, maskSize)
      ? []
      : [{ asset: 'photo', colorOptionId: color.colorOptionId, size: photo.photoSize, maskSize }];
  });
  return [...assets, ...photos];
};
